import React, { useEffect, useRef, useState } from 'react';

interface ScrollFadeContainerProps {
  children: React.ReactNode;
  className?: string;
  scrollClassName?: string;
}

/** Scroll area with top/bottom fade overlays (Kanban-style). */
const ScrollFadeContainer: React.FC<ScrollFadeContainerProps> = ({
  children,
  className = '',
  scrollClassName = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const threshold = 2;
    setShowTopFade(scrollTop > threshold);
    setShowBottomFade(scrollTop < scrollHeight - clientHeight - threshold);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    checkScroll();
    scrollElement.addEventListener('scroll', checkScroll);
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className={`min-h-0 relative ${className}`}>
      {showTopFade && (
        <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white/95 via-white/60 to-transparent backdrop-blur-[1px] pointer-events-none z-10 transition-opacity duration-300" />
      )}
      {showBottomFade && (
        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white/95 via-white/60 to-transparent backdrop-blur-[1px] pointer-events-none z-10 transition-opacity duration-300" />
      )}
      <div
        ref={scrollRef}
        data-scroll-fade-viewport
        className={`h-full overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-none ${scrollClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default ScrollFadeContainer;
